// ./src/components/features/Connectors/ConfigEditors/ConvertKitConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Switch,
  Group,
  Box,
  List,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconMail,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { ConvertKitConnectorConfig } from "@/types";

export interface ConvertKitConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: ConvertKitConnectorConfig = {
  apiSecret: "",
  formId: "",
  tagId: "",
  autoMapFields: true,
};

/**
 * Parse and validate ConvertKit connector configuration
 */
const parseConvertKitConfig = (
  jsonString: string
): ConvertKitConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const ConvertKitConnectorConfigEditor: React.FC<
  ConvertKitConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<ConvertKitConnectorConfig>(() =>
    parseConvertKitConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseConvertKitConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: ConvertKitConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleApiSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, apiSecret: e.target.value });
  };

  const handleFormIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, formId: e.target.value });
  };

  const handleTagIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, tagId: e.target.value });
  };

  const handleAutoMapFieldsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, autoMapFields: e.currentTarget.checked });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconMail size={16} />} color="red" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://app.convertkit.com/account_settings/advanced_settings"
              target="_blank"
              rel="noopener noreferrer"
            >
              ConvertKit → Settings → Advanced
            </Anchor>
          </List.Item>
          <List.Item>
            Copy the <strong>API Secret</strong> (not API Key)
          </List.Item>
          <List.Item>Get your Form ID from the form's settings page</List.Item>
        </List>
      </Alert>

      {/* API Secret Field */}
      <TextInput
        label="API Secret"
        placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
        description="Found in Settings → Advanced → API Secret"
        value={config.apiSecret}
        onChange={handleApiSecretChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Form ID Field */}
      <TextInput
        label="Form ID"
        placeholder="1234567"
        description="Numeric ID of the form to subscribe to"
        value={config.formId}
        onChange={handleFormIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Help for Form ID */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Where to find Form ID:
        </Text>
        <List size="xs" spacing={2}>
          <List.Item>
            Go to <strong>Landing Pages & Forms</strong>
          </List.Item>
          <List.Item>Click on your form</List.Item>
          <List.Item>
            The ID is in the URL: <strong>/forms/1234567/edit</strong>
          </List.Item>
        </List>
      </Alert>

      {/* Tag ID Field */}
      <TextInput
        label="Tag ID (Optional)"
        placeholder="1234567"
        description="Add a tag to the subscriber"
        value={config.tagId || ""}
        onChange={handleTagIdChange}
        disabled={disabled}
        size="md"
      />

      {/* Help for Tag ID */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Where to find Tag ID:
        </Text>
        <List size="xs" spacing={2}>
          <List.Item>
            Go to <strong>Subscribers → Tags</strong>
          </List.Item>
          <List.Item>Click on a tag</List.Item>
          <List.Item>
            The ID is in the URL: <strong>/subscribers?tags[]=1234567</strong>
          </List.Item>
        </List>
      </Alert>

      {/* Auto Map Fields Toggle */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Auto-Map Fields</Text>
            <Text size="xs" c="dimmed">
              Automatically map form fields to first_name
            </Text>
          </div>
          <Switch
            checked={config.autoMapFields !== false}
            onChange={handleAutoMapFieldsChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Requirements */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="orange"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Requirements:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Form <strong>must have an email field</strong>
          </List.Item>
          <List.Item>
            Custom fields (form_submission_id, form_name, submission_url) must
            be created in ConvertKit to store submission data
          </List.Item>
        </List>
      </Alert>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>Email field is used as the subscriber email</List.Item>
          <List.Item>First name is auto-detected from form fields</List.Item>
          <List.Item>
            Custom fields store: form_submission_id, form_name, submission_url
          </List.Item>
          <List.Item>Optional tag is added after subscription</List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
